// Angular Files
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

// Teller Online Library Files
import {
    HelpServiceBase,
    HelpArticle,
    HelpConditional,
    HelpConditionContext,
    HelpTag,
    InfoMessageCollection
} from 'teller-online-libraries/help';
import { TellerOnlineSiteMetadataService } from 'teller-online-libraries/core';
import { TellerOnlineIconsService } from 'teller-online-libraries/icons';

// Payment Integration Files
import { isRedirectProcessor } from 'apps/public-portal/src/app/payment-integrations/base/service/redirect-processors';

/**
 * Help tags specific to the config portal
 */
export enum ConfigHelpTag {
    Site = "site",
    MenuItems = "menu-items",
    Pages = "pages",
    EmailTemplates = "templates-email",
    SmsTemplates = "templates-sms",
    ImportExport = "import-export",
    TellerConnections = "teller-connections"
}

/**
 * Help article groups within tags for the config portal.
 * Used to sub-categorize articles under a single tag.
 */
export enum ConfigHelpGroup {
    HomepageSettings = "Homepage Settings",
    CheckoutSettings = "Checkout Settings",
    GlobalEmailSettings = "Global Email Settings",
    SearchPages = "Search Pages",
    ItemPages = "Item Pages",
    InboundRedirect = "Inbound Redirect (Teller Online Lite)",
    ScheduledPayments = "Scheduled Payments"
}

@Injectable({
    providedIn: 'root'
})
export class ConfigHelpService extends HelpServiceBase {
    private _context: HelpConditionContext;

    constructor(
        sanitizer: DomSanitizer,
        private iconService: TellerOnlineIconsService,
        private siteMetadataService: TellerOnlineSiteMetadataService
    ) {
        super(sanitizer);

        const creditProcessor = this.siteMetadataService.appConfiguration?.creditProcessor;

        this._context = {
            tenantName: this.siteMetadataService.tenant,
            processorName: creditProcessor || '',
            isRedirect: isRedirectProcessor(creditProcessor),
            authenticatedUsersEnabled: this.siteMetadataService.authenticationEnabled,
            tellerOnlineLiteEnabled: this.siteMetadataService.tellerOnlineLiteEnabled,
            scheduledPaymentsEnabled: this.siteMetadataService.scheduledPaymentsEnabled
        };

        this.initializeArticles((iconName) => this.iconService.loadIcon(iconName));
    }

    protected getConditionContext(): HelpConditionContext {
        return this._context;
    }

    protected getArticleDefinitions(): HelpArticle[] {
        return CONFIG_HELP_ARTICLES;
    }

    protected override getInfoMessageDefinitions(): InfoMessageCollection {
        return CONFIG_INFO_MESSAGES;
    }

    public determineTagByUrl(currentUrl: string): HelpTag {
        // Determine which tag to show based on the current URL for config portal
        if (currentUrl.includes("config/site")) {
            return ConfigHelpTag.Site;
        } else if (currentUrl.includes("config/menu-items")) {
            return ConfigHelpTag.MenuItems;
        } else if (currentUrl.includes("config/pages")) {
            return ConfigHelpTag.Pages;
        } else if (currentUrl.includes("config/email-templates")) {
            return ConfigHelpTag.EmailTemplates;
        } else if (currentUrl.includes("config/sms-templates")) {
            return ConfigHelpTag.SmsTemplates;
        } else if (currentUrl.includes("config/teller-connections")) {
            return ConfigHelpTag.TellerConnections;
        } else {
            // Default to site configuration for other config pages
            return ConfigHelpTag.Site;
        }
    }

    public getAvailableTags(): HelpTag[] {
        // Return all tags from the ConfigHelpTag enum
        return Object.values(ConfigHelpTag);
    }
}

const CONFIG_HELP_ARTICLES: HelpArticle[] = [
    // =========================================================================
    // SITE CONFIGURATION
    // =========================================================================
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 0,
        bookmark: 'site-overview',
        title: 'Site Configuration Overview',
        body: '<p>The Site Configuration page allows you to customize the appearance and behavior of your public portal. It is divided into three collapsible sections:</p> ' +
            '<ul>' +
            '<li><strong>Homepage Settings:</strong> Branding, colors, logo, menu items, banner content, and footer links.</li>' +
            '<li><strong>Checkout Settings:</strong> Default country/state and cart disclaimers.</li>' +
            '<li><strong>Global Email Settings:</strong> The "From" display name and email footer used across all outgoing emails.</li>' +
            '</ul>' +
            '<p>Changes made here directly affect how end users see and interact with the payment portal.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 1,
        bookmark: 'how-to-edit',
        title: 'How do I edit anything?',
        body: '<p>To edit content on the Site page:</p> ' +
            '<ol>' +
            '<li>Open the section you want to edit (Homepage Settings, Checkout Settings, or Global Email Settings). Note, the Homepage Settings is open by default.</li>' +
            '<li>Click the <strong>Edit</strong> button in the top-left corner of that section.</li>' +
            '<li>Once in edit mode, hover over any editable element in the preview area. Editable fields will display a <strong>dashed blue border</strong> when you hover over them.</li>' +
            '<li>Click the element to begin editing it and a dialog will open to edit it inline.</li>' +
            '<li>When finished, click <strong>Save</strong> to persist the changes inline.</li>' +
            '<li>When you are done making all of your changes, click <strong>Save Changes</strong> at the top-left of the section for them to take effect for the public.</li>' +
            '</ol>' +
            '<p><strong>Note:</strong> Required fields that are empty or invalid will display a <strong>dashed red border</strong> to indicate they need attention before you can save.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 2,
        bookmark: 'one-section-at-a-time',
        title: 'Why can\'t I open more than one section at once?',
        body: '<p>The Site page uses an accordion layout, meaning only one section can be open at a time. When you open a new section, the previously open section will automatically close. This keeps the page focused and prevents confusion when editing.</p>' +
            '<p>If you are currently in edit mode on a section, you must exit edit mode (or save your changes) before you can open a different section.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 3,
        bookmark: 'edit-not-working',
        title: 'I\'m clicking to edit something and it\'s not working',
        body: '<p>If clicking on an editable element is not opening an editor, it is likely because you already have an inline edit dialog open somewhere else on the page. Only one inline editor can be active at a time.</p>' +
            '<p>Look for an open text input, dropdown, or editor elsewhere in the preview area. Save or cancel that edit first, and then you will be able to click on the next element you want to edit.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 4,
        bookmark: 'storage-service-unreachable',
        title: 'What does "Storage Service Unreachable" mean?',
        body: '<p>This warning banner appears when the system cannot connect to the file storage service that hosts your site assets (logo, favicon, and custom styles for colors). This is typically a temporary infrastructure issue.</p>' +
            '<p>While the storage service is unreachable:</p> ' +
            '<ul>' +
            '<li>Your logo and favicon may appear as placeholder images in the preview.</li>' +
            '<li>You may not be able to upload new assets.</li>' +
            '<li>Your public portal will continue to use the last successfully uploaded assets.</li>' +
            '</ul>' +
            '<p>If this issue persists, please contact support.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 5,
        group: ConfigHelpGroup.HomepageSettings,
        bookmark: 'cant-reorder-menu-items-site',
        title: 'Why can\'t I reorder menu items from the site page?',
        body: '<p>The Site page allows you to add, edit, and inactivate menu items, but does not support drag-and-drop reordering. To reorder menu items, navigate to the <strong>Menu Items</strong> page instead, which provides a dedicated table view with drag-and-drop reordering support.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 6,
        group: ConfigHelpGroup.HomepageSettings,
        bookmark: 'action-color',
        title: 'What is "Action Color"?',
        body: '<p><strong>Action Color</strong> is the primary accent color used throughout your public portal. It is applied to:</p> ' +
            '<ul>' +
            '<li>Buttons and interactive elements</li>' +
            '<li>Links and highlights</li>' +
            '<li>Focus indicators and form accents</li>' +
            '</ul>' +
            '<p>When you set the action color, a full Material Design color palette is automatically generated from it, providing consistent shading across the entire portal.</p>' +
            '<p>The action color is checked against <strong>white</strong> to ensure it meets the <strong>WCAG AA contrast ratio of 4.5:1</strong> for accessibility.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 7,
        group: ConfigHelpGroup.HomepageSettings,
        bookmark: 'banner-link',
        title: 'What is a "Banner Link"?',
        body: '<p>A <strong>Banner Link</strong> is any hyperlink within the home page banner area. The color defaults to a lighter version of the action color but is separate from the general action color so that links in the banner can be styled to contrast properly against the banner background.</p>' +
            '<p>The banner foreground and banner link colors are checked against the <strong>banner background</strong> to ensure they meet the <strong>WCAG AA contrast ratio of 4.5:1</strong> for accessibility.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 8,
        group: ConfigHelpGroup.HomepageSettings,
        bookmark: 'banner-link-contrast',
        title: 'I don\'t have any links in my banner. Why do I need a banner link color?',
        body: '<p>The contrast check for the banner link color is enforced regardless of whether your banner currently contains links. This is because:</p> ' +
            '<ul>' +
            '<li>Banner content can be changed at any time, and links may be added in the future.</li>' +
            '<li>The banner link color may also be used for other interactive elements within the banner area.</li>' +
            '</ul>' +
            '<p>To resolve this, simply adjust the banner link color to meet the 4.5:1 contrast ratio against your banner background color, even if you do not currently have links in the banner. You can use the real-time contrast indicators in the color toolbar to find a suitable color.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 9,
        group: ConfigHelpGroup.HomepageSettings,
        bookmark: 'upload-logo-favicon',
        title: 'How do I upload a new logo or favicon?',
        body: '<p>To upload a new logo or favicon:</p> ' +
            '<ol>' +
            '<li>Open the <strong>Homepage Settings</strong> section.</li>' +
            '<li>Click <strong>Edit</strong> to enter edit mode.</li>' +
            '<li>Click directly on the <strong>logo image</strong> (in the header area of the preview) or the <strong>favicon image</strong> (in the browser tab area of the preview).</li>' +
            '<li>A file dialog will open. Select an appropriate image file.</li>' +
            '<li>The preview will update immediately with your new image.</li>' +
            '<li>Click <strong>Save Changes</strong> to upload and apply the new image.</li>' +
            '</ol>' +
            '<p><strong>Recommended formats:</strong></p> ' +
            '<ul>' +
            '<li><strong>Logo:</strong> PNG, at least 56 pixels tall (112 pixels for high-DPI displays)</li>' +
            '<li><strong>Favicon:</strong> ICO, 32x32 pixels</li>' +
            '</ul>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 10,
        group: ConfigHelpGroup.HomepageSettings,
        bookmark: 'logo-favicon-not-showing',
        title: 'My changes to the logo/favicon aren\'t showing up immediately?',
        body: '<p>After uploading a new logo or favicon, the changes may not appear right away on the public portal due to <strong>browser caching</strong>. Your browser and any intermediate caches may continue serving the old image for a period of time.</p>' +
            '<p>To see the updated image immediately:</p> ' +
            '<ul>' +
            '<li>Perform a <strong>hard refresh</strong> of the page (Ctrl+Shift+R or Cmd+Shift+R).</li>' +
            '<li>Clear your browser cache.</li>' +
            '<li>Open the portal in an incognito/private browsing window.</li>' +
            '</ul>' +
            '<p>The admin portal preview will show your new image immediately after upload, but the public portal may take a few minutes to reflect the change.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 11,
        group: ConfigHelpGroup.CheckoutSettings,
        bookmark: 'multi-payment-disclaimer',
        title: 'What is the Multi-Payment Disclaimer?',
        conditions: [HelpConditional.NotRedirect],
        body: '<p>The <strong>multi-payment disclaimer</strong> is a message displayed to users when they have multiple items in their cart that will be split into separate payments. It informs users about why their cart is split into multiple payments and how that will impact any fees.</p>' +
            '<p>This disclaimer appears on the checkout page in the header. You can edit it in the <strong>Checkout Settings</strong> section by entering edit mode and clicking on the multi-payment disclaimer text in the header.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 12,
        group: ConfigHelpGroup.CheckoutSettings,
        bookmark: 'disclaimer',
        title: 'What is a Cart Disclaimer?',
        body: '<p>The <strong>cart disclaimer</strong> is a general message displayed to users during checkout. It can contain terms, conditions, or important notices that users should be aware of before completing their payment. Only the first 2 lines of the disclaimer are shown without a user needing to click for more information which will be rendered in a modal dialog.</p>' +
            '<p>The disclaimer is shown in the cart sidebar throughout the website. To edit it, open the <strong>Checkout Settings</strong> section, enter edit mode, and click the disclaimer area to open the editing dialog.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 13,
        group: ConfigHelpGroup.CheckoutSettings,
        bookmark: 'default-country-state',
        title: 'Can I change the default country and state?',
        conditions: [HelpConditional.NotRedirect],
        body: '<p>Yes. The default country and state are editable in the <strong>Checkout Settings</strong> section. These defaults pre-populate the billing address form on the checkout page for your users, saving them time if most of your users are from the same area.</p>' +
            '<p>To change them, enter edit mode in the Checkout Settings section and use the country and state dropdowns in the checkout preview.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 14,
        group: ConfigHelpGroup.GlobalEmailSettings,
        bookmark: 'email-display-name',
        title: 'Can I change the display name for the "From" email?',
        body: '<p>Yes. The <strong>From email display name</strong> is editable in the <strong>Global Email Settings</strong> section. This is the name that appears in the "From" field of all outgoing emails (e.g., "City Payments" instead of just the email address).</p>' +
            '<p>If you do not provide a display name, recipients will simply see the raw email address as the sender.</p>' +
            '<p>To change it, enter edit mode in the Global Email Settings section and click on the display name text to edit it inline.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 15,
        group: ConfigHelpGroup.GlobalEmailSettings,
        bookmark: 'from-email-address',
        title: 'Can I change the "From" email address?',
        body: '<p>The <strong>"From" email address</strong> is configured outside of this portal and cannot be changed here. If you need to change the email address that outgoing emails are sent from, please contact support for assistance.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Site,
        order: 16,
        group: ConfigHelpGroup.GlobalEmailSettings,
        bookmark: 'email-footer',
        title: 'What is the Email Footer?',
        body: '<p>The <strong>email footer</strong> is an HTML-formatted block of text that can be included at the bottom of outgoing customer-facing emails. It is edited using a rich text editor in the <strong>Global Email Settings</strong> section.</p>' +
            '<p>The footer is provided to Email Templates as the <strong>{EMAILFOOTERTEXT}</strong> variable. It will only appear in an email if the template for that email includes the {EMAILFOOTERTEXT} variable in its body. Not all templates include it by default, so you may need to add the variable to the templates where you want the footer to appear.</p>'
    }),

    // =========================================================================
    // MENU ITEMS
    // =========================================================================
    new HelpArticle({
        tag: ConfigHelpTag.MenuItems,
        order: 0,
        bookmark: 'menu-items-overview',
        title: 'Menu Items Overview',
        body: '<p>The Menu Items page is an alternate place to manage the navigation menu items for your public portal. It provides all the same functionality as managing menu items on the Site page, with the added benefit of <strong>drag-and-drop reordering</strong>.</p>' +
            '<p>You can:</p> ' +
            '<ul>' +
            '<li>Create new menu items</li>' +
            '<li>Edit existing menu items by clicking on them</li>' +
            '<li>Toggle menu item visibility (active/inactive)</li>' +
            '<li>Reorder menu items by dragging rows in the table</li>' +
            '</ul>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.MenuItems,
        order: 1,
        bookmark: 'menu-item-label',
        title: 'What is the Label on a Menu Item?',
        body: '<p>The <strong>label</strong> is the primary text displayed on the menu button in the public portal. It is what your end users will see and click on to navigate to the linked page. The label is a required field and should be concise and descriptive (e.g., "Pay Property Tax", "Utility Bills").</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.MenuItems,
        order: 2,
        bookmark: 'menu-item-description',
        title: 'What is the Description on a Menu Item?',
        body: '<p>The <strong>description</strong> on a menu item is an optional text field that provides additional context about the menu item. It can help a user make the right choice when clicking on a menu item, such as briefly explaining what an invoice is.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.MenuItems,
        order: 3,
        bookmark: 'menu-item-page',
        title: 'Why does a menu item need a page?',
        body: '<p>Each menu item has a <strong>Page</strong> field that determines where the user is taken when they click the menu button. Without a linked page, the menu button would have nowhere to navigate to.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.MenuItems,
        order: 4,
        bookmark: 'menu-item-pages-first',
        title: 'Why do pages need to be created before menu items?',
        body: '<p>The Page dropdown on a menu item only lists pages that already exist. If you need a menu item to point to a page that doesn\'t exist yet, you must create the page on the <strong>Pages</strong> configuration page before creating or editing the menu item.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.MenuItems,
        order: 5,
        bookmark: 'menu-item-missing-pages',
        title: 'Why can I only see some pages when editing a menu item?',
        body: '<p>The Page dropdown only shows <strong>active</strong> pages. If a page has been inactivated, it will not appear as an option.</p>' +
            '<p>If a menu item was previously linked to a page that has since been inactivated, a warning icon will appear next to the page name in the menu items list.</p>'
    }),

    // =========================================================================
    // TELLER CONNECTIONS
    // =========================================================================
    new HelpArticle({
        tag: ConfigHelpTag.TellerConnections,
        order: 0,
        bookmark: 'cant-add-connections',
        title: 'Why can\'t I add teller connections?',
        body: '<p>Only <strong>super users</strong> can add new teller connections. If you do not see the "Create New" button on the Teller Connections page, your account does not have super user privileges.</p>' +
            '<p>Contact support to have a teller connection added or to request super user access.</p>'
    }),
    // NOTE: Commented out for now because adding connections is restricted to super users only.
    // Once non-super-user creation is supported, uncomment this article.
    // new HelpArticle({
    //     tag: ConfigHelpTag.TellerConnections,
    //     order: 1,
    //     bookmark: 'connection-permissions-for-adding',
    //     title: 'Which teller connections can I add?',
    //     body: '<p>When adding a new teller connection, you can only add connections for systems for which you have both <strong>ModifyTellerOnlineSite</strong> and <strong>UseApi</strong> permissions. If you have already configured all systems you have these permissions for, you will receive a notification indicating there are no more systems available to add.</p>' +
    //         '<p>Contact support if you need permissions for additional systems.</p>'
    // }),
    new HelpArticle({
        tag: ConfigHelpTag.TellerConnections,
        order: 2,
        bookmark: 'cant-see-connection',
        title: 'Why can\'t I see a particular teller connection?',
        body: '<p>You can only see and manage teller connections for which you have the <strong>ModifyTellerOnlineSite</strong> permission <em>and</em> the <strong>UseApi</strong> permission. If a teller connection is not appearing in your list, it is likely because you do not have one or both of these permissions for that connection\'s teller system.</p>' +
            '<p>Contact support to have the appropriate permissions assigned to your account.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.TellerConnections,
        order: 3,
        bookmark: 'system-uniqueness',
        title: 'Can I configure the same teller system multiple times?',
        body: '<p>Yes, you can configure the same teller system multiple times. Each configuration is a "Teller Connection" and must have a unique <strong>Name</strong>.</p>' +
            '<p>Be aware that if you change the location and/or payment channel settings on an existing teller connection mid-day, you will need to <strong>close out the open session</strong> for that day before any subsequent payments can be taken.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.TellerConnections,
        order: 4,
        bookmark: 'session-end-time',
        title: 'What is "Session End Time"?',
        body: '<p>The <strong>Session End Time</strong> field specifies when the daily session should automatically cut off. It should be entered in 24-hour format: <strong>HH&#58;MM&#58;SS</strong> (e.g., 23&#58;59&#58;59).</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.TellerConnections,
        order: 5,
        bookmark: 'session-name-prefix',
        title: 'What is "Session Name Prefix"?',
        conditions: [HelpConditional.ScheduledPayments],
        body: '<p>The <strong>Session Name Prefix</strong> is a short label (up to 8 characters) prepended to the session name when this teller connection processes scheduled payments.</p>' +
            '<p>This field is only needed for teller connections that are assigned as the <strong>Scheduled Payment Teller Connection</strong> on a search page. If a teller connection is not used for scheduled payments, this field can be left blank.</p>' +
            '<p>The prefix helps distinguish scheduled payment sessions from regular payment sessions in Teller reports and logs.</p>' +
            '<p>For normal payments, the session name prefix is managed in <strong>Teller Admin</strong>.</p>'
    }),

    // =========================================================================
    // PAGES
    // =========================================================================
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 0,
        bookmark: 'page-header',
        title: 'What is a Page Header and where does it appear?',
        body: '<p>The <strong>header</strong> is the title of the page. On the public portal, it is displayed as a large heading at the top of the page. For search pages, the word "Lookup" is automatically appended after the header text (e.g., a header of "Property Tax" would display as "Property Tax Lookup").</p>' +
            '<p>The header is a required field when creating or editing a page.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 1,
        bookmark: 'page-description',
        title: 'Where does the Page Description appear?',
        body: '<p>The <strong>description</strong> is an optional text field that appears directly below the header on the public portal. It provides additional context or instructions for users on that page.</p>' +
            '<p>If no description is provided, nothing is shown below the header.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 2,
        bookmark: 'cant-inactivate-page',
        title: 'Why can\'t I inactivate this page?',
        body: '<p>A page cannot be inactivated if it is currently referenced by one or more <strong>active menu items</strong>. When you attempt to inactivate such a page, you will see a message listing the menu items that reference it.</p>' +
            '<p>To inactivate the page, you must first inactivate or remove the menu items that link to it. You can do this from the <strong>Menu Items</strong> page or the <strong>Site</strong> page.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 3,
        bookmark: 'cant-change-connection',
        title: 'Why can\'t I change the teller connection on a page?',
        body: '<p>Once a page has been created, its <strong>teller connection</strong> assignment cannot be changed. The teller connection dropdown is locked (disabled) in edit mode because changing it could break in-flight payments, affect existing payment records, and impact permissions that control who can see which payments.</p>' +
            '<p>If you need a page associated with a different teller connection, you will need to create a new page with the correct teller connection selected.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 4,
        group: ConfigHelpGroup.SearchPages,
        bookmark: 'search-pages',
        title: 'What are Search Pages?',
        body: '<p><strong>Search Pages</strong> allow users to search for items to pay using integrations with external business systems. Each search page is connected to a specific integration (plugin), and can contain one or more search types that define how users search for their items.</p>' +
            '<p>For example, a search page might let users look up property tax bills by account number, or search for utility bills by address.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 5,
        group: ConfigHelpGroup.SearchPages,
        bookmark: 'integration-broken',
        title: 'Why is my integration showing a number instead of a name?',
        body: '<p>If the integration column on a search page is displaying a raw number instead of a readable name, it means the <strong>plugin in Teller is not properly configured</strong> for that system. This can happen if:</p> ' +
            '<ul>' +
            '<li>The plugin was removed from the Teller system.</li>' +
            '<li>The plugin configuration was inactivated in Teller.</li>' +
            '</ul>' +
            '<p>Contact support to verify that the plugin is still active and properly configured for the system associated with this page.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 6,
        group: ConfigHelpGroup.SearchPages,
        bookmark: 'cant-change-integration',
        title: 'Why can\'t I change the integration on a page?',
        body: '<p>Similar to the system, the <strong>integration</strong> (plugin) on a search page is locked after the page is created. Changing the integration could break both in-flight and past payments associated with the page, as those payments are tied to the original integration.</p>' +
            '<p>If you need a different integration, create a new search page with the correct system and integration selected.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 7,
        group: ConfigHelpGroup.SearchPages,
        bookmark: 'paginated-results',
        title: 'What is the "Paginated Results" toggle?',
        body: '<p>The <strong>Paginated Results</strong> toggle controls whether search results on a search page are displayed with pagination (showing a limited number of results per page with navigation to see more) rather than showing all results at once.</p>' +
            '<p>Enable this option if searches are likely to return a large number of results and you want to improve page performance and readability.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 8,
        group: ConfigHelpGroup.InboundRedirect,
        bookmark: 'inbound-redirect-source',
        title: 'What is an Inbound Redirect Source?',
        body: '<p>An <strong>Inbound Redirect Source</strong> is part of Teller Online Lite (TOL) and allows external systems to redirect users to your public portal in order to seamlessly pay for something from an external system.</p>' +
            '<p>Some examples include ACA (Accela Citizens Access Portal), OPAL (Oracle Permitting and Licensing), and even a city or county website. You can collect data from the user on your own site, and redirect to the public portal in order to process the payment for it.</p>' +
            '<p>This feature requires <strong>Teller Online Lite</strong> to be enabled for your site. If you do not see the Inbound Redirect Source toggle on your search page (or its disabled), contact support to request access.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 10,
        group: ConfigHelpGroup.InboundRedirect,
        bookmark: 'tol-alias',
        title: 'What is the "Alias"?',
        conditions: [HelpConditional.TOL],
        body: '<p>The <strong>Alias</strong> is a unique, URL-friendly identifier for the inbound redirect source. It is used as part of the URL that external systems will use to redirect users to this page.</p>' +
            '<p>For example, if you are using ACA (Accela Citizens Access Portal) as your inbound redirect source, the default URL would be <strong>telleronline.net/TOL/ACA</strong>. If you set an alias of "MyCoolWebsite", the URL would become <strong>telleronline.net/TOL/MyCoolWebsite</strong>.</p>' +
            '<p>The alias should be short, descriptive, and unique across all your inbound redirect sources. It is automatically suggested based on your source key selection but can be customized.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 11,
        group: ConfigHelpGroup.InboundRedirect,
        bookmark: 'tol-label',
        title: 'What is the "Label"?',
        conditions: [HelpConditional.TOL],
        body: '<p>The <strong>Label</strong> is a human-readable display name for the inbound redirect source. It is used within the admin portal for identification purposes and is shown to users on the public portal during the redirect flow.</p>' +
            '<p>On the public portal, the label appears in the "Return to [Label]" button on the checkout success page after a payment is completed, as well as in error messages if something goes wrong during the process. This helps users understand where they will be taken back to.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 12,
        group: ConfigHelpGroup.InboundRedirect,
        bookmark: 'tol-security-key',
        title: 'What is the "Security Key" and is it required?',
        conditions: [HelpConditional.TOL],
        body: '<p>The <strong>Security Key</strong> is a GUID (globally unique identifier) used to validate and secure requests from external systems. When provided, the external system must include this key in its redirect request for it to be accepted.</p>' +
            '<p>The security key is <strong>optional</strong> but strongly recommended. If you do not provide one, incoming redirect requests will not be validated against a key. The only reason to omit a security key is if the external system you are integrating with does not support it.</p>' +
            '<p>Known integrations that do not support security keys include <strong>OPAL</strong> (Oracle Permitting and Licensing) and <strong>EPL</strong> (EnerGov Permitting and Licensing).</p>' +
            '<p>You can use the auto-generate button next to the field to create a new GUID automatically.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 13,
        group: ConfigHelpGroup.InboundRedirect,
        bookmark: 'tol-payment-option-type',
        title: 'What is the "Payment Option Type"?',
        conditions: [HelpConditional.TOL],
        body: '<p>The <strong>Payment Option Type</strong> specifies which payment option(s) will be used to process payments from this inbound redirect source.</p>' +
            '<p>You can configure more than one payment option type for a single source. The user is never given a choice — the external system determines which payment option to use on each redirect request.</p>' +
            '<p>For example, an OPAL (Oracle Permitting and Licensing) source might be configured as <strong>Full,Custom</strong> because some redirects are for a fee payment (Full) while others are for a payment on an account (Custom).</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 14,
        group: ConfigHelpGroup.InboundRedirect,
        bookmark: 'tol-url-properties',
        title: 'What are all these URL properties?',
        conditions: [HelpConditional.TOL],
        body: '<p>The URL properties define where users are redirected after their payment flow completes. There are three configurable URLs:</p> ' +
            '<ul>' +
            '<li><strong>Success URL:</strong> Where the user is redirected after a successful payment.</li>' +
            '<li><strong>Cancel URL:</strong> Where the user is redirected if they cancel the payment process.</li>' +
            '<li><strong>Error URL:</strong> Where the user is redirected if an error occurs during the payment process.</li>' +
            '</ul>' +
            '<p>All three URLs are optional. If not provided, the user will simply be taken to the portal\'s home page.</p>' +
            '<p>These URLs can also be passed as query string parameters on the incoming redirect (e.g., <strong>successUrl</strong>, <strong>cancelUrl</strong>, <strong>errorUrl</strong>). Query string parameters take priority over the configured values here.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 15,
        group: ConfigHelpGroup.InboundRedirect,
        bookmark: 'tol-show-error-dialog',
        title: 'What is the "Show Error Dialog" option?',
        conditions: [HelpConditional.TOL],
        body: '<p>The <strong>Show Error Dialog</strong> option controls how errors are displayed to users who arrive via an inbound redirect. When enabled, errors will be shown in a modal dialog overlay on the page rather than redirecting the user to a separate error page or the configured error URL.</p>' +
            '<p>Typically you would enable this in a scenario where you do not intend to redirect the user to the originating site.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 16,
        group: ConfigHelpGroup.InboundRedirect,
        bookmark: 'tol-allow-cart-item-details',
        title: 'What does "Allow Cart Item Details Page" mean?',
        conditions: [HelpConditional.TOL],
        body: '<p>When enabled, <strong>Allow Cart Item Details Page</strong> permits users who arrive via an inbound redirect to view the detailed information page for items in their cart.</p>'+
            '<p>When disabled, users can only see the name of the thing they are paying for in the cart and are unable to click through for additional details.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 17,
        group: ConfigHelpGroup.SearchPages,
        bookmark: 'search-types',
        title: 'What is a Search Type?',
        body: '<p>A <strong>search type</strong> defines a way for users to search for items on a search page. Each search page can have multiple search types, which appear as selectable options (radio buttons) on the public portal.</p>' +
            '<p>For example, a property tax search page might have search types for "Search by Account Number" and "Search by Address", each querying the same system but using different fields.</p>' +
            '<p>Each search type is configured with a <strong>Teller Fetch Type</strong> or <strong>Fetch Template</strong> that determines which fields are used for the search, and optional instructional content to help users.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 18,
        group: ConfigHelpGroup.SearchPages,
        bookmark: 'fetch-type',
        title: 'What is a Teller Fetch Type?',
        body: '<p>A <strong>Teller Fetch Type</strong> is a single search field defined by the integration/plugin in Teller (e.g., "AccountNo", "InvoiceNo", "Address"). When you specify a fetch type, the search form will present that single field for the user to search on.</p>' +
            '<p>The list of available fetch types depends on the integration. Please contact support for the list of fetch types supported by your integration.</p>' +
            '<p>If you need to search on multiple fields, add validation, or have more control over the search behavior, you will need to use a <strong>Teller Fetch Template</strong> instead.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 19,
        group: ConfigHelpGroup.SearchPages,
        bookmark: 'fetch-template',
        title: 'What is a Teller Fetch Template?',
        body: '<p>A <strong>Teller Fetch Template</strong> is a custom template string that provides more control over search behavior than a single Teller Fetch Type. Templates allow you to:</p>' +
            '<ul><li>Combine multiple fetch types into a single search operation (e.g., searching by both account number and invoice number)</li>' +
            '<li>Add validation rules, such as requiring a search value to match a specific pattern (e.g., regex)</li>' +
            '<li>Define which fields are required and which are optional</li></ul>' +
            '<p>Configure the template in the search type detail panel by providing the template string that references the desired fetch types. A <strong>Teller Fetch Type</strong> is still required when using a template, but it is only used for the URL rather than defining the search fields.</p>' +
            '<p>For example, a search on both account number and invoice number might have a Teller Fetch Type of "InvoiceDetails" to represent the broader search context in the URL.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 20,
        group: ConfigHelpGroup.SearchPages,
        bookmark: 'search-type-instructions',
        title: 'What are the Instructions Title, Text, and Image on a Search Type?',
        body: '<p>Each search type can include optional instructional content to help users understand how to search:</p> ' +
            '<ul>' +
            '<li><strong>Instructions Title:</strong> The clickable link text displayed below the search form on the public portal (e.g., "Where do I find my Customer # and Invoice #?"). When clicked, it opens a dialog containing the instructions text and image.</li>' +
            '<li><strong>Instructions Text:</strong> Descriptive text shown inside the instructions dialog, explaining how to use the search or where to find the information needed (e.g., "Your account number can be found in the upper-right corner of your bill").</li>' +
            '<li><strong>Instructions Image:</strong> A visual aid shown below the instructions text in the dialog, such as a photo or diagram of a sample bill with the relevant field highlighted. Users can click the image to view it at full size.</li>' +
            '<li><strong>Instructions Image Alt Text:</strong> Alternative text for the instructions image, used by screen readers for accessibility and displayed when the image fails to load.</li>' +
            '</ul>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 21,
        group: ConfigHelpGroup.SearchPages,
        bookmark: 'lookahead-search',
        title: 'What is a Lookahead Search?',
        body: '<p>A <strong>lookahead search</strong> (also known as typeahead or autocomplete) provides search suggestions to users as they type. Instead of waiting for the user to submit a search, the system queries results in real-time and displays matching options in a dropdown.</p>' +
            '<p>When enabled on a search type, you can also specify the <strong>lookahead field</strong>, which determines which field is used for the autocomplete matching.</p>' +
            '<p>This feature improves the user experience for searches where users may not know the exact value they need to enter.</p>' +
            '<p><strong>Note:</strong> Lookahead search must be supported by the desired integration. By default, most integrations do not support this feature. Please reach out to support for a full list of which integrations support lookahead search.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 22,
        group: ConfigHelpGroup.ItemPages,
        bookmark: 'item-pages',
        title: 'What are Item Pages?',
        body: '<p><strong>Item Pages</strong> display standalone items that users can select and pay for directly without needing to search. Unlike search pages, item pages do not use an integration/plugin. Instead, the items, forms, and pricing are all configured within Teller Online.</p>' +
            '<p>A common use case is payments that require the user to fill out a form, such as a Transient Occupancy Tax form. Item pages are also useful for fixed-price services, fees, donations, or any other payment that doesn\'t require searching an external system.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 23,
        group: ConfigHelpGroup.ItemPages,
        bookmark: 'item-type-vs-teller',
        title: 'What is an Item Type?',
        body: '<p>An <strong>item type</strong> is a payable item displayed on an item page. Each item type is linked to an <strong>Item Type</strong> in Teller, which controls the core payment behavior (configured in Teller, not here).</p>' +
            '<p>What you configure here in Teller Online is the presentation layer. These control how the item appears to users on the public portal:</p> ' +
            '<ul>' +
            '<li><strong>Label:</strong> The name displayed to users on the item page.</li>' +
            '<li><strong>Image:</strong> A visual representation of the item.</li>' +
            '<li><strong>Help Text:</strong> Descriptive text shown on the item details page.</li>' +
            '<li><strong>Notification Settings:</strong> Who gets notified when the item is purchased.</li>' +
            '</ul>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 24,
        group: ConfigHelpGroup.ItemPages,
        bookmark: 'item-type-teller-settings',
        title: 'What do I need to configure in Teller?',
        body: '<p>The core behavior of an item type is controlled by its <strong>Teller item configuration</strong>, which must be set up in Teller. The following Teller settings affect how the item works on the public portal:</p> ' +
            '<ul>' +
            '<li><strong>Quantity:</strong> If enabled in Teller, users can specify a quantity for the item.</li>' +
            '<li><strong>Form:</strong> If configured in Teller, an additional form is presented to collect information from the user.</li>' +
            '<li><strong>DefaultAmount:</strong> If set in Teller, this is the fixed price of the item.</li>' +
            '<li><strong>Description:</strong> The Teller item description is used in the cart to identify the item.</li>' +
            '</ul>' +
            '<p><strong>Note:</strong> The following Teller item properties are <strong>not used</strong> by TellerOnline:</p> ' +
            '<ul>' +
            '<li><strong>DisplayName</strong> and <strong>Code</strong> are not displayed.</li>' +
            '<li><strong>RequireDescription</strong> and <strong>Taxable</strong> are incompatible with TellerOnline and should not be configured.</li>' +
            '</ul>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 25,
        group: ConfigHelpGroup.ItemPages,
        bookmark: 'item-type-image',
        title: 'What is the Image for on an Item Type?',
        body: '<p>The <strong>image</strong> on an item type is a visual representation of the item displayed to users on the public portal. It helps users identify the item at a glance.</p>' +
            '<p>The image appears in two places: on the item listing/search page as a thumbnail beside the item name, and on the individual item details page at a larger size.</p>' +
            '<p>Images can be uploaded in the item type detail panel.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 26,
        group: ConfigHelpGroup.ItemPages,
        bookmark: 'help-text',
        title: 'Where does Help Text appear?',
        body: '<p>The <strong>help text</strong> on an item type is descriptive text that appears on the public portal\'s item details page only. It is displayed in the banner at the top of the page, directly beneath the item name.</p>' +
            '<p>It provides additional context about the item, such as what it is for, who should pay it, or any relevant details. Help text does not appear on the item listing/search page.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 27,
        group: ConfigHelpGroup.ItemPages,
        bookmark: 'internal-notification',
        title: 'What is an Internal Notification Email/Template?',
        body: '<p>The <strong>internal notification email</strong> is an email address that receives an automatic notification whenever a user purchases an item of that type. This allows administrators or staff to be immediately informed of a purchase.</p>' +
            '<p>The <strong>internal notification template</strong> is the HTML email body used for these notifications. It supports two variables:</p> ' +
            '<ul>' +
            '<li><strong>{ITEMNAME}</strong> - The name of the item that was purchased.</li>' +
            '<li><strong>{CUSTOMVALUES}</strong> - Any custom field values collected from the user during purchase.</li>' +
            '</ul>' +
            '<p>This is useful for items that require manual follow-up, such as service requests or donations that need acknowledgment.</p>'
    }),

    // =========================================================================
    // SCHEDULED PAYMENTS (on search pages)
    // =========================================================================
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 28,
        group: ConfigHelpGroup.ScheduledPayments,
        bookmark: 'scheduled-payments',
        title: 'What are Scheduled Payments on a search page?',
        conditions: [HelpConditional.ScheduledPayments],
        body: '<p><strong>Scheduled Payments</strong> allow users to set up recurring or future-dated payments for items on a search page. When enabled on a search page, users can schedule payments to be processed automatically on specified dates.</p>' +
            '<p>Scheduled payments use a separate <strong>Teller Connection</strong> and <strong>Integration</strong> from the page\'s primary ones. This allows scheduled payments to be processed through a different system configuration than real-time payments.</p>' +
            '<p>This feature requires the <strong>Scheduled Payments</strong> portal feature to be enabled for your site. If the toggle is disabled, contact support to request access.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 29,
        group: ConfigHelpGroup.ScheduledPayments,
        bookmark: 'scheduled-payments-toggle-disabled',
        title: 'Why is the Scheduled Payments toggle disabled?',
        body: '<p>The <strong>Scheduled Payments</strong> toggle is disabled when the Scheduled Payments portal feature is not enabled for your site. This is a site-wide setting that must be activated before scheduled payments can be configured on individual pages.</p>' +
            '<p>Contact support to have the Scheduled Payments feature enabled for your site.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 30,
        group: ConfigHelpGroup.ScheduledPayments,
        bookmark: 'scheduled-payment-connection',
        title: 'What is the Scheduled Payment Teller Connection?',
        conditions: [HelpConditional.ScheduledPayments],
        body: '<p>The <strong>Scheduled Payment Teller Connection</strong> specifies which teller connection should be used when processing scheduled payments for a search page. This can be the same as or different from the page\'s primary teller connection.</p>' +
            '<p>Using a separate connection allows scheduled payment sessions to be tracked independently from real-time payment sessions. The teller connection assigned here should have a <strong>Session Name Prefix</strong> configured to help distinguish scheduled payment sessions in reports.</p>' +
            '<p>If set to <strong>None</strong>, scheduled payments will not have an explicit connection override.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 31,
        group: ConfigHelpGroup.ScheduledPayments,
        bookmark: 'scheduled-payment-integration',
        title: 'What is the Scheduled Payment Integration?',
        conditions: [HelpConditional.ScheduledPayments],
        body: '<p>The <strong>Scheduled Payment Integration</strong> specifies which plugin should be used when processing scheduled payments for a search page. The available integrations are filtered based on the selected Scheduled Payment Teller Connection.</p>' +
            '<p>You must select a Scheduled Payment Teller Connection before choosing an integration. If set to <strong>None</strong>, scheduled payments will not have an explicit integration override.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.Pages,
        order: 32,
        group: ConfigHelpGroup.ScheduledPayments,
        bookmark: 'scheduled-payment-columns',
        title: 'What do the "/ values" mean in the pages list?',
        conditions: [HelpConditional.ScheduledPayments],
        body: '<p>When scheduled payments are configured on a search page, the <strong>Teller Connection</strong> and <strong>Integration</strong> columns in the pages list show both the primary and scheduled payment values separated by a <strong>/</strong> character.</p>' +
            '<p>For example, <strong>Online / Scheduled</strong> in the Teller Connection column means the page\'s primary connection is "Online" and its scheduled payment connection is "Scheduled".</p>' +
            '<p>If a search page does not have scheduled payment settings configured, only the primary value is shown.</p>'
    }),

    // =========================================================================
    // EMAIL TEMPLATES
    // =========================================================================
    new HelpArticle({
        tag: ConfigHelpTag.EmailTemplates,
        order: 0,
        bookmark: 'email-templates-overview',
        title: 'Email Templates Overview',
        body: '<p>Email templates control the content of automated emails sent to users. They support rich HTML formatting via a <strong>WYSIWYG editor</strong> with a variable insertion dropdown.</p>' +
            '<p>Each template type has its own set of available variables that are replaced with actual values when the email is sent.</p>' +
            '<p>The email footer configured in <strong>Global Email Settings</strong> can be included in any email template using the <strong>{EMAILFOOTERTEXT}</strong> variable.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.EmailTemplates,
        order: 1,
        bookmark: 'email-template-variables',
        title: 'How do template variables work?',
        body: '<p>Template variables are placeholders that get replaced with actual values when the email is sent. They are written in curly braces, e.g., <strong>{PAYMENTAMOUNT}</strong>.</p>' +
            '<p>The editor includes a variable insertion dropdown that shows all available variables for the current template type. Click a variable to insert it at the cursor position.</p>' +
            '<p>At send time, the system replaces each variable with its actual value (e.g., {PAYMENTAMOUNT} becomes "$150.00").</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.EmailTemplates,
        order: 2,
        bookmark: 'email-payment-successful-template',
        title: 'Payment Successful Template',
        body: '<p>The <strong>Payment Successful Template</strong> is sent to users immediately after a successful payment is completed. It serves as a payment confirmation and receipt notification.</p>' +
            '<p><strong>When it\'s sent:</strong> After the payment transaction is successfully processed.</p>' +
            '<p><strong>Available variables:</strong></p>' +
            '<ul>' +
            '<li><strong>{PAYMENTAMOUNT}</strong> - The payment amount</li>' +
            '<li><strong>{CONVENIENCEFEE}</strong> - The convenience/processing fee charged</li>' +
            '<li><strong>{TOTALCHARGED}</strong> - The total amount charged (payment + fee)</li>' +
            '<li><strong>{RECEIPTNO}</strong> - The receipt number for the transaction</li>' +
            '<li><strong>{SIGNUPURL}</strong> - A link for the user to sign up for an account. Will only be inserted if the payment was made by an anonymous user</li>' +
            '<li><strong>{EMAILFOOTERTEXT}</strong> - The email footer configured in Global Email Settings</li>' +
            '</ul>' +
            '<p><strong>Note:</strong> A receipt PDF will automatically be attached to the email.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.EmailTemplates,
        order: 3,
        bookmark: 'email-forgot-password-template',
        title: 'Forgot Password Template',
        body: '<p>The <strong>Forgot Password Template</strong> is intended to be sent when a user initiates a password reset by clicking "Forgot Password" on the login page.</p>' +
            '<p><strong>Note:</strong> This template is not currently in use. The <strong>Sign Up Template</strong> is used for forgot password emails instead. ' +
            'This template is reserved for future use.</p>' +
            '<p><strong>Available variables:</strong></p>' +
            '<ul>' +
            '<li><strong>{USERNAME}</strong> - The user\'s name or email</li>' +
            '<li><strong>{####}</strong> - The numeric confirmation code</li>' +
            '<li><strong>{EMAILFOOTERTEXT}</strong> - The email footer</li>' +
            '</ul>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.EmailTemplates,
        order: 4,
        bookmark: 'email-reset-password-template',
        title: 'Reset Password Template',
        body: '<p>The <strong>Reset Password Template</strong> is intended to be sent after a user\'s password has been successfully reset.</p>' +
            '<p><strong>Note:</strong> This template is not currently in use. The <strong>Sign Up Template</strong> is used for password reset emails instead. ' +
            'This template is reserved for future use.</p>' +
            '<p><strong>Available variables:</strong></p>' +
            '<ul>' +
            '<li><strong>{USERNAME}</strong> - The user\'s name or email</li>' +
            '<li><strong>{####}</strong> - The confirmation code</li>' +
            '<li><strong>{EMAILFOOTERTEXT}</strong> - The email footer</li>' +
            '</ul>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.EmailTemplates,
        order: 5,
        bookmark: 'email-deferred-item-template',
        title: 'Deferred Item Notification Template',
        body: '<p>The <strong>Deferred Item Notification Template</strong> is intended to notify users about deferred payment items.</p>' +
            '<p><strong>Note:</strong> This template is not currently in use. The template can be configured in advance, but no emails will be sent until this feature is enabled in a future update.</p>' +
            '<p><strong>When it\'s sent:</strong> When a deferred payment notification is triggered for a user.</p>' +
            '<p><strong>Available variables:</strong></p>' +
            '<ul>' +
            '<li><strong>{PAYMENTAMOUNT}</strong> - The payment amount</li>' +
            '<li><strong>{RECEIPTNO}</strong> - The receipt number</li>' +
            '<li><strong>{EMAILFOOTERTEXT}</strong> - The email footer</li>' +
            '</ul>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.EmailTemplates,
        order: 6,
        bookmark: 'email-payment-due-template',
        title: 'Payment Due Notification Template',
        body: '<p>The <strong>Payment Due Notification Template</strong> is intended to remind users that a payment is due.</p>' +
            '<p><strong>Note:</strong> This template is not currently in use. The template can be configured in advance, but no emails will be sent until this feature is enabled in a future update.</p>' +
            '<p><strong>When it\'s sent:</strong> When a scheduled payment due notification is triggered.</p>' +
            '<p><strong>Available variables:</strong></p>' +
            '<ul>' +
            '<li><strong>{PAYMENTAMOUNT}</strong> - The payment amount due</li>' +
            '<li><strong>{CONVENIENCEFEE}</strong> - The convenience/processing fee</li>' +
            '<li><strong>{TOTALCHARGED}</strong> - The total amount</li>' +
            '<li><strong>{RECEIPTNO}</strong> - The receipt number</li>' +
            '<li><strong>{EMAILFOOTERTEXT}</strong> - The email footer</li>' +
            '</ul>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.EmailTemplates,
        order: 7,
        bookmark: 'email-sign-up-template',
        title: 'Sign Up Template',
        body: '<p>The <strong>Sign Up Template</strong> is used for any email that includes a 6-digit confirmation code. Despite its name, it is not limited to sign-up.</p>' +
            '<p><strong>When it\'s sent:</strong></p>' +
            '<ul>' +
            '<li>New user registration (email verification)</li>' +
            '<li>Changing an email address (re-verification)</li>' +
            '<li>Forgot password (password reset code)</li>' +
            '</ul>' +
            '<p><strong>Available variables:</strong></p>' +
            '<ul>' +
            '<li><strong>{USERNAME}</strong> - The user\'s name or email</li>' +
            '<li><strong>{####}</strong> - The verification confirmation code</li>' +
            '<li><strong>{EMAILFOOTERTEXT}</strong> - The email footer</li>' +
            '</ul>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.EmailTemplates,
        order: 8,
        bookmark: 'email-sign-up-confirmed-template',
        title: 'Sign Up for Confirmed User Template',
        body: '<p>The <strong>Sign Up for Confirmed User Template</strong> is sent to an existing confirmed user when someone attempts to sign up using their email address. This notifies the user of the sign-up attempt.</p>' +
            '<p><strong>When it\'s sent:</strong> When a sign-up is attempted with an email that already belongs to a confirmed user.</p>' +
            '<p><strong>Available variables:</strong></p>' +
            '<ul>' +
            '<li><strong>{USERNAME}</strong> - The user\'s name or email</li>' +
            '<li><strong>{PUBLICPORTALURL}</strong> - A link to the public portal</li>' +
            '<li><strong>{EMAILFOOTERTEXT}</strong> - The email footer</li>' +
            '</ul>'
    }),

    // =========================================================================
    // SMS TEMPLATES
    // =========================================================================
    new HelpArticle({
        tag: ConfigHelpTag.SmsTemplates,
        order: 0,
        bookmark: 'sms-templates-overview',
        title: 'SMS Templates Overview',
        body: '<p>SMS templates control the content of automated text messages sent to users. They are <strong>plain text</strong> with a <strong>160-character limit</strong>.</p>' +
            '<p>The editor includes a variable insertion dropdown for inserting available variables. An estimated character counter shows the approximate message length after variables are expanded.</p>' +
            '<p>Each SMS template type has its own set of available variables that may differ from the email version of the same template type.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.SmsTemplates,
        order: 1,
        bookmark: 'sms-template-variables',
        title: 'How do template variables work?',
        body: '<p>Template variables are placeholders that get replaced with actual values when the SMS is sent. They are written in curly braces, e.g., <strong>{PAYMENTAMOUNT}</strong>.</p>' +
            '<p>The editor includes a variable insertion dropdown that shows all available variables for the current template type. Click a variable to insert it at the cursor position.</p>' +
            '<p>Because SMS messages are limited to 160 characters, the editor shows an <strong>estimated character count</strong> that accounts for the approximate length of expanded variables. This helps you stay within the limit. For example:</p>' +
            '<ul>' +
            '<li><strong>{NAME}</strong> - estimated at 20 characters</li>' +
            '<li><strong>{PAYMENTAMOUNT}</strong> - estimated at 10 characters (e.g., "$15,000.00")</li>' +
            '<li><strong>{CONVENIENCEFEE}</strong> - estimated at 6 characters</li>' +
            '<li><strong>{TOTALCHARGED}</strong> - estimated at 10 characters</li>' +
            '<li><strong>{RECEIPTNO}</strong> - estimated at 20 characters</li>' +
            '<li><strong>{RECEIPTURL}</strong> - estimated dynamically (e.g., "https://telleronline.net/download-receipt/a1b2c3d4-e5f6-7890-abcd-ef1234567890")</li>' +
            '<li><strong>{####}</strong> - estimated at 6 characters</li>' +
            '<li><strong>{USERNAME}</strong> - estimated at 20 characters</li>' +
            '</ul>' +
            '<p>At send time, the system replaces each variable with its actual value.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.SmsTemplates,
        order: 2,
        bookmark: 'sms-payment-successful-template',
        title: 'Payment Successful Template',
        body: '<p>The <strong>Payment Successful Template</strong> is sent to users immediately after a successful payment is completed. It serves as a brief payment confirmation via text message.</p>' +
            '<p><strong>When it\'s sent:</strong> After the payment transaction is successfully processed.</p>' +
            '<p><strong>Available variables:</strong></p>' +
            '<ul>' +
            '<li><strong>{NAME}</strong> - The user\'s full name</li>' +
            '<li><strong>{PAYMENTAMOUNT}</strong> - The payment amount</li>' +
            '<li><strong>{CONVENIENCEFEE}</strong> - The convenience/processing fee charged</li>' +
            '<li><strong>{TOTALCHARGED}</strong> - The total amount charged (payment + fee)</li>' +
            '<li><strong>{RECEIPTNO}</strong> - The receipt number for the transaction</li>' +
            '<li><strong>{RECEIPTURL}</strong> - A link to download the receipt</li>' +
            '</ul>' +
            '<p><strong>Tip:</strong> The {RECEIPTURL} variable expands to a full URL which can be quite long. Keep this in mind when composing your template to stay within the 160-character limit. The estimated character counter accounts for this.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.SmsTemplates,
        order: 3,
        bookmark: 'sms-deferred-item-template',
        title: 'Deferred Item Notification Template',
        body: '<p>The <strong>Deferred Item Notification Template</strong> is intended to notify users about deferred payment items via SMS.</p>' +
            '<p><strong>Note:</strong> SMS sending for this template type is not yet implemented. The template can be configured in advance, but no SMS messages will be sent until this feature is enabled in a future update.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.SmsTemplates,
        order: 4,
        bookmark: 'sms-payment-due-template',
        title: 'Payment Due Notification Template',
        body: '<p>The <strong>Payment Due Notification Template</strong> is intended to remind users via SMS that a payment is due.</p>' +
            '<p><strong>Note:</strong> SMS sending for this template type is not yet implemented. The template can be configured in advance, but no SMS messages will be sent until this feature is enabled in a future update.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.SmsTemplates,
        order: 5,
        bookmark: 'sms-verify-phone-template',
        title: 'Verify Phone Template',
        body: '<p>The <strong>Verify Phone Template</strong> is sent when a user needs to verify their phone number.</p>' +
            '<p><strong>When it\'s sent:</strong> When a user adds or changes their phone number and needs to verify it.</p>' +
            '<p>This template is unique to SMS and has no email equivalent.</p>' +
            '<p><strong>Available variables:</strong></p>' +
            '<ul>' +
            '<li><strong>{####}</strong> - The numeric verification code</li>' +
            '<li><strong>{USERNAME}</strong> - The user\'s name or email</li>' +
            '</ul>'
    }),
    // =========================================================================
    // IMPORT/EXPORT
    // =========================================================================
    new HelpArticle({
        tag: ConfigHelpTag.ImportExport,
        order: 0,
        bookmark: 'export-overview',
        title: 'How do I export my configuration?',
        body: '<p>Exporting allows you to back up your site configuration or prepare it for transfer to another environment (e.g., from staging to production).</p>' +
            '<ol>' +
            '<li>Click the <strong>Import/Export Config</strong> button in the header.</li>' +
            '<li>Click <strong>Export</strong> to download a ZIP file containing your complete site configuration.</li>' +
            '</ol>' +
            '<p>The export includes all site settings, systems, pages (with search types, item types, and inbound redirect sources), menu items, email templates, SMS templates, and branding assets (logo, favicon, custom styles, and images).</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.ImportExport,
        order: 1,
        bookmark: 'import-overview',
        title: 'How do I import a configuration?',
        body: '<p>Importing allows you to apply a previously exported configuration to your site.</p>' +
            '<ol>' +
            '<li>Click the <strong>Import/Export Config</strong> button in the header.</li>' +
            '<li>Click <strong>Import</strong> and select a previously exported ZIP file.</li>' +
            '<li>The system will analyze the file and show you a detailed <strong>preview</strong> of all changes that will be made, comparing the incoming configuration with your current settings.</li>' +
            '<li>Review the changes carefully. Each entity will be marked as <strong>New</strong>, <strong>Updated</strong>, or <strong>Unchanged</strong>, with specific property changes listed.</li>' +
            '<li>Click <strong>Confirm Import</strong> to apply the changes.</li>' +
            '</ol>' +
            '<p><strong>Warning:</strong> Importing will overwrite existing configuration. It is strongly recommended to export your current configuration as a backup before importing.</p>'
    }),
    new HelpArticle({
        tag: ConfigHelpTag.ImportExport,
        order: 2,
        bookmark: 'import-file-types',
        title: 'What file types are allowed for import?',
        body: '<p>Only <strong>ZIP files</strong> (.zip) are accepted for import. The ZIP file must have been generated by the export feature from this portal or a compatible environment. Uploading any other file type will result in an error.</p>'
    })
];

/**
 * Info messages for the config portal.
 * These are displayed in info dialogs when users click info icons,
 * separate from the help articles shown in the help sidebar.
 */
const CONFIG_INFO_MESSAGES: InfoMessageCollection = {
    // Pages configuration
    PAGES: {
        SEARCH_PAGES: {
            title: 'Search Pages',
            message: 'Search Pages allow users to search for items using integrations with external systems. Each search page can contain multiple search types, each with their own instructions and accompanying instructional images.'
        },
        ITEM_PAGES: {
            title: 'Item Pages',
            message: 'Item Pages display standalone items that users can select directly without searching. Each item page can contain multiple item types with their own images and descriptions.'
        },
        INBOUND_REDIRECT_SOURCE: {
            title: 'Inbound Redirect Source',
            message: 'Inbound Redirect Source (TOL) allows external systems to securely redirect users to this search page with pre-populated parameters. When enabled, you can configure the security key, URLs, and other settings for the integration.'
        }
    },

    // Consent Texts
    AUTHORIZATION_TEXTS: {
        OVERVIEW: {
            title: 'Consent Texts',
            message: 'Consent texts presented to users when they submit a payment. E-check variants are required by NACHA and cannot be deactivated; credit-card variants are optional and fall back to the ad-hoc text when not configured.'
        },
        CONSENT_POLICY: {
            title: 'Consent Policy',
            message: 'Controls how the authorization disclosure is presented to payers site-wide. Inline shows the disclosure above the submit button and the click is the consent (clickwrap); Modal shows it in a dialog the payer confirms.'
        }
    },

    // Site configuration
    SITE: {
        HOMEPAGE_SETTINGS: {
            title: 'Homepage Settings',
            message: 'Manage your public portal\'s home page appearance: logo, favicon, site title, banner content, colors (action color, banner background/foreground/link), menu items, and footer links. Open the Help sidebar for more details on each setting.'
        },
        CHECKOUT_SETTINGS: {
            title: 'Checkout Settings',
            message: 'Manage the default country and state for billing address pre-population (if applicable), as well as cart and multi-payment disclaimers shown during checkout. Open the Help sidebar for more details on each setting.'
        },
        GLOBAL_EMAIL_SETTINGS: {
            title: 'Global Email Settings',
            message: 'Manage the "From" display name shown on all outgoing emails and the email footer content. The footer can be included in email templates using the {EMAILFOOTERTEXT} variable. Open the Help sidebar for more details on each setting.'
        },
        FROM_EMAIL_ADDRESS: {
            title: 'From Email Address',
            message: 'To change the from email address, please contact support'
        }
    }
};
